// Blog article template ("Editorial", design picked by Ali from Higgsfield concepts, Oct 2026).
// Full-bleed photo hero, reading column + sticky sidebar (live rate, contents, visit card),
// key-takeaways box, FAQ accordion, more guides. Content is the markdown subset from
// blog-agent/STYLE.md, stored in Mongo blog_posts.content.
import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import axios from "axios";
import { applyMeta } from "@/components/Seo";
import { decryptRates } from "@/utils/crypto";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = "https://www.fonderx.com";

// ---------- markdown subset ----------
const linkCls = "text-blue-300 underline decoration-blue-400/40 underline-offset-[3px] hover:text-white hover:decoration-white";
function inline(text, keyBase = "i") {
  const out = [];
  const re = /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let last = 0, m, k = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const key = `${keyBase}-${k++}`;
    if (m[1]) out.push(m[2].startsWith("/")
      ? <Link key={key} to={m[2]} className={linkCls}>{m[1]}</Link>
      : <a key={key} href={m[2]} target="_blank" rel="noopener noreferrer" className={linkCls}>{m[1]}</a>);
    else if (m[3]) out.push(<strong key={key} className="text-white font-semibold">{m[3]}</strong>);
    else out.push(<em key={key}>{m[4]}</em>);
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
const isTable = (b) => b.split("\n").length >= 2 && /^\s*\|/.test(b) && /^\s*\|?\s*:?-{3,}/.test(b.split("\n")[1]);
const cells = (row) => row.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
const isList = (b) => b.split("\n").every((l) => /^- /.test(l));
const slugId = (s) => "s-" + s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);
const strip = (s) => s.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1").replace(/\*\*?/g, "");

function Block({ b, i }) {
  if (b.startsWith("## ")) {
    const t = b.slice(3);
    return (
      <h2 id={slugId(t)} className="scroll-mt-28 mt-14 mb-5 text-[1.65rem] md:text-3xl font-bold text-white tracking-tight">
        <span className="block w-10 h-[3px] bg-blue-500 rounded-full mb-4" aria-hidden="true" />{inline(t, `h${i}`)}
      </h2>);
  }
  if (b.startsWith("### ")) return <h3 className="mt-9 mb-3 text-xl font-semibold text-white">{inline(b.slice(4), `h${i}`)}</h3>;
  if (/^\*\*[^*]+\*\*$/.test(b)) return <h3 className="mt-9 mb-3 text-xl font-semibold text-white">{b.replace(/\*\*/g, "")}</h3>;
  if (isTable(b)) {
    const rows = b.split("\n").filter((r, idx) => idx !== 1 && r.trim());
    return (
      <div className="my-8 overflow-x-auto rounded-xl border border-white/10 bg-[#001a40]">
        <table className="w-full text-left text-[0.95rem]">
          <thead><tr className="border-b border-white/10">{cells(rows[0]).map((c, j) =>
            <th key={j} className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-blue-200/70">{inline(c, `t${i}${j}`)}</th>)}</tr></thead>
          <tbody>{rows.slice(1).map((r, ri) => (
            <tr key={ri} className="border-b border-white/5 last:border-0 even:bg-white/[0.025]">{cells(r).map((c, j) =>
              <td key={j} className={`px-5 py-3.5 tabular-nums ${j === 0 ? "text-white font-medium" : "text-blue-100/80"}`}>{inline(c, `t${i}${ri}${j}`)}</td>)}</tr>))}
          </tbody>
        </table>
      </div>);
  }
  if (isList(b))
    return <ul className="my-6 space-y-3">{b.split("\n").map((l, j) => (
      <li key={j} className="relative pl-6 text-[1.075rem] leading-relaxed text-blue-50/80">
        <span className="absolute left-0 top-[0.7em] w-2 h-2 rounded-full bg-blue-500" aria-hidden="true" />{inline(l.slice(2), `l${i}${j}`)}</li>))}</ul>;
  if (b.split("\n").every((l) => /^\d+\. /.test(l)))
    return <ol className="my-6 list-decimal pl-6 space-y-3 text-[1.075rem] leading-relaxed text-blue-50/80 marker:text-blue-400 marker:font-semibold">
      {b.split("\n").map((l, j) => <li key={j} className="pl-1">{inline(l.replace(/^\d+\. /, ""), `o${i}${j}`)}</li>)}</ol>;
  return <p className="mb-6 text-[1.075rem] leading-[1.8] text-blue-50/80">{inline(b, `p${i}`)}</p>;
}

// body / key takeaways / FAQ / photo credit
function parse(content) {
  const blocks = content.replace(/\r/g, "").split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
  let credit = null;
  if (blocks.length && blocks[blocks.length - 1].startsWith("Photo:")) credit = blocks.pop();
  const fi = blocks.findIndex((b) => /^## (frequently asked questions|faq)/i.test(b));
  let body = fi === -1 ? blocks : blocks.slice(0, fi);
  const faq = [];
  if (fi !== -1) for (const b of blocks.slice(fi + 1)) {
    if (b.startsWith("### ")) faq.push({ q: b.slice(4), a: [] });
    else if (faq.length) faq[faq.length - 1].a.push(b);
  }
  let takeaways = null;
  const ti = body.findIndex((b) => /^## key takeaways/i.test(b));
  if (ti !== -1 && body[ti + 1] && isList(body[ti + 1])) {
    takeaways = body[ti + 1].split("\n").map((l) => l.slice(2));
    body = [...body.slice(0, ti), "%%TAKEAWAYS%%", ...body.slice(ti + 2)];
  }
  const toc = body.filter((b) => b.startsWith("## ")).map((b) => b.slice(3));
  return { body, faq, credit, takeaways, toc };
}

const CUR_WORDS = [["us dollar", "USD"], ["usd", "USD"], ["euro", "EUR"], ["british pound", "GBP"], ["pound", "GBP"], ["yen", "JPY"], ["mexic", "MXN"],
  ["peso", "MXN"], ["yuan", "CNY"], ["rupee", "INR"], ["philippine", "PHP"], ["hong kong", "HKD"], ["won", "KRW"], ["baht", "THB"], ["dong", "VND"],
  ["australian", "AUD"], ["new zealand", "NZD"], ["dirham", "AED"], ["lira", "TRY"], ["swiss", "CHF"], ["dinar", "IQD"], ["bitcoin", "BTC"], ["tether", "USDT"], ["usdt", "USDT"]];
function guessCurrency(post) {
  const hay = `${post.title} ${post.slug}`.toLowerCase().replace(/-/g, " ");
  const hit = CUR_WORDS.find(([w]) => hay.includes(w));
  return hit ? hit[1] : "USD";
}
function category(post) {
  const s = `${post.slug} ${post.title}`.toLowerCase();
  if (/rate.*(week|report)|exchange rates in/.test(s)) return "Rate report";
  if (/bitcoin|crypto|usdt|tether|ethereum/.test(s)) return "Crypto";
  if (/trip|travel|vacation|bring|holiday/.test(s)) return "Travel money";
  return "Guides";
}
const fmtRate = (x) => (x >= 1000 ? x.toLocaleString("en-CA", { maximumFractionDigits: 2 }) : x >= 1 ? x.toFixed(4) : x.toFixed(6));

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) { if (el) el.remove(); return; }
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
}

function RateCard({ code, rate, compact }) {
  if (!rate) return null;
  return (
    <div className={`rounded-2xl border border-white/10 bg-[#001a40] ${compact ? "p-4" : "p-5"}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-blue-200/60">Today at FonderX</span>
        <span className="text-sm font-bold text-white">{code} / CAD</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-white/[0.04] p-3"><div className="text-xs text-blue-200/60 mb-1">We buy</div>
          <div className="text-xl font-bold tabular-nums text-emerald-400">{fmtRate(rate.sell_rate)}</div></div>
        <div className="rounded-xl bg-white/[0.04] p-3"><div className="text-xs text-blue-200/60 mb-1">We sell</div>
          <div className="text-xl font-bold tabular-nums text-rose-400">{fmtRate(rate.buy_rate)}</div></div>
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-blue-200/50">
        <span>Live board, CAD per 1 {code}</span>
        <Link to="/" className="font-semibold text-blue-300 hover:text-white">All rates →</Link>
      </div>
    </div>);
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [more, setMore] = useState([]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    axios.get(`${API}/blog/${slug}`).then((r) => alive && setPost(r.data)).catch(() => alive && setError("Post not found")).finally(() => alive && setLoading(false));
    axios.get(`${API}/blog`).then((r) => alive && setMore(r.data.filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
    axios.get(`${API}/x7k9`).then((r) => alive && setRates(decryptRates(r.data.d) || [])).catch(() => {});
    return () => { alive = false; };
  }, [slug]);

  const parsed = useMemo(() => (post ? parse(post.content) : null), [post]);

  useEffect(() => {
    if (!post) return;
    const text = (post.excerpt || post.content || "").replace(/[#*_>`[\]]/g, "").replace(/\s+/g, " ").trim();
    applyMeta({ title: `${post.title} | FonderX`, description: text.length > 160 ? text.slice(0, 157).replace(/\s+\S*$/, "") + "…" : text });
    const img = post.image_url ? (post.image_url.startsWith("http") ? post.image_url : SITE + post.image_url).split("?")[0] : undefined;
    setJsonLd("ld-article", {
      "@context": "https://schema.org", "@type": "BlogPosting", headline: post.title, description: post.excerpt, image: img ? [img] : undefined,
      datePublished: post.created_at, dateModified: post.updated_at || post.created_at,
      author: { "@type": "Organization", name: "FonderX Currency Exchange", url: SITE },
      publisher: { "@type": "Organization", name: "FonderX Currency Exchange Inc.", logo: { "@type": "ImageObject", url: `${SITE}/logo192.png` } },
      mainEntityOfPage: `${SITE}/blog/${post.slug}`,
    });
    const { faq } = parse(post.content);
    setJsonLd("ld-faq", faq.length ? { "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: faq.map((f) => ({ "@type": "Question", name: strip(f.q), acceptedAnswer: { "@type": "Answer", text: strip(f.a.join(" ")) } })) } : null);
    return () => { setJsonLd("ld-article", null); setJsonLd("ld-faq", null); };
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#001435]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <Skeleton className="h-4 w-32 mb-6 bg-white/10" /><Skeleton className="h-14 w-3/4 mb-4 bg-white/10" /><Skeleton className="h-6 w-1/2 mb-16 bg-white/10" />
          <Skeleton className="h-4 w-full max-w-2xl mb-3 bg-white/10" /><Skeleton className="h-4 w-full max-w-2xl mb-3 bg-white/10" />
        </div>
      </div>);
  }
  if (error || !post) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="text-2xl font-bold text-white mb-2">Post not found</h1>
          <p className="text-blue-100/60 mb-6">This article doesn't exist or was moved.</p>
          <Link to="/blog" className="btn-primary inline-flex items-center"><ArrowLeft className="w-4 h-4 mr-2" /> All guides</Link>
        </div>
      </div>);
  }

  const { body, faq, credit, takeaways, toc } = parsed;
  const code = guessCurrency(post);
  const rate = rates.find((r) => r.currency_code === code);
  const date = new Date(post.created_at).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  const mins = Math.max(1, Math.round(post.content.split(/\s+/).length / 230));
  const img = post.image_url;

  return (
    <div className="bg-[#001435]" data-testid="blog-post">
      {/* Hero: photo with navy wash */}
      <header className="relative overflow-hidden border-b border-white/10">
        {img && <img src={img} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-r from-[#001435] via-[#001435]/90 to-[#001435]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#001435] via-transparent to-[#001435]/30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14 md:pt-14 md:pb-20">
          <nav className="flex items-center gap-2 text-sm text-blue-200/60 mb-10" aria-label="Breadcrumb">
            <Link to="/blog" className="inline-flex items-center gap-1.5 hover:text-white"><ArrowLeft className="w-4 h-4" />Guides</Link>
            <span aria-hidden="true">/</span><span className="text-blue-200/80">{category(post)}</span>
          </nav>
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-400 mb-4">{category(post)}</p>
            <h1 className="text-[2.1rem] leading-[1.12] md:text-5xl lg:text-[3.4rem] font-extrabold text-white tracking-tight" style={{ textWrap: "balance" }}>{post.title}</h1>
            {post.excerpt && <p className="mt-6 text-lg md:text-xl leading-relaxed text-blue-100/75 max-w-2xl">{post.excerpt}</p>}
            <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-blue-200/60">
              <span className="font-semibold text-white/90">{post.author}</span><span aria-hidden="true">·</span>
              <time dateTime={post.created_at}>{date}</time><span aria-hidden="true">·</span><span>{mins} min read</span>
            </div>
          </div>
          {credit && <p className="mt-10 md:mt-0 md:absolute md:right-8 md:bottom-4 text-[0.7rem] text-blue-200/40 max-w-xs md:text-right">{inline(credit, "cr")}</p>}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-16">
        <article className="min-w-0 max-w-[700px]">
          <div className="lg:hidden mb-10"><RateCard code={code} rate={rate} compact /></div>

          {body.map((b, i) => b === "%%TAKEAWAYS%%" ? (
            <aside key={i} className="my-10 rounded-2xl border border-blue-400/20 bg-blue-500/[0.07] p-6 md:p-7">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-300 mb-4">Key takeaways</p>
              <ul className="space-y-3">{takeaways.map((t, j) => (
                <li key={j} className="relative pl-6 text-blue-50/90 leading-relaxed">
                  <span className="absolute left-0 top-[0.62em] w-2 h-2 rounded-full bg-blue-400" aria-hidden="true" />{inline(t, `k${j}`)}</li>))}</ul>
            </aside>) : <Block key={i} b={b} i={i} />)}

          {faq.length > 0 && (
            <section className="mt-16" aria-labelledby="faq">
              <h2 id="faq" className="scroll-mt-28 text-[1.65rem] md:text-3xl font-bold text-white tracking-tight mb-6">
                <span className="block w-10 h-[3px] bg-blue-500 rounded-full mb-4" aria-hidden="true" />Frequently asked questions</h2>
              <div className="divide-y divide-white/10 border-y border-white/10">
                {faq.map((f, i) => (
                  <details key={i} className="group py-1" open={i === 0}>
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-4 text-left text-lg font-semibold text-white [&::-webkit-details-marker]:hidden">
                      <span>{inline(f.q, `fq${i}`)}</span>
                      <ChevronDown className="w-5 h-5 shrink-0 text-blue-300 transition-transform group-open:rotate-180" aria-hidden="true" />
                    </summary>
                    <div className="pb-5 pr-10">{f.a.map((a, j) => <p key={j} className="text-blue-50/75 leading-relaxed">{inline(a, `fa${i}${j}`)}</p>)}</div>
                  </details>))}
              </div>
            </section>)}
        </article>

        {/* Sidebar */}
        <aside className="hidden lg:block">
          <div className="sticky top-28 space-y-6">
            <RateCard code={code} rate={rate} />
            {toc.length > 1 && (
              <nav className="rounded-2xl border border-white/10 p-5" aria-label="In this article">
                <p className="text-xs font-semibold uppercase tracking-wider text-blue-200/60 mb-3">In this article</p>
                <ol className="space-y-2.5 text-sm">
                  {toc.map((t, i) => (
                    <li key={i}><a href={`#${slugId(t)}`} className="flex gap-3 text-blue-100/70 hover:text-white">
                      <span className="tabular-nums text-blue-400/70">{String(i + 1).padStart(2, "0")}</span><span>{strip(t)}</span></a></li>))}
                  {faq.length > 0 && <li><a href="#faq" className="flex gap-3 text-blue-100/70 hover:text-white">
                    <span className="tabular-nums text-blue-400/70">{String(toc.length + 1).padStart(2, "0")}</span><span>FAQ</span></a></li>}
                </ol>
              </nav>)}
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#002055] to-[#001a40] p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-blue-200/60 mb-3">Visit FonderX</p>
              <p className="text-white font-semibold leading-snug">Unit 2, 723 12th Street<br />New Westminster, BC V3M 4J8</p>
              <dl className="mt-4 space-y-1.5 text-sm">
                <div className="flex justify-between"><dt className="text-blue-200/60">Mon – Fri</dt><dd className="text-white tabular-nums">9:30 AM – 6:00 PM</dd></div>
                <div className="flex justify-between"><dt className="text-blue-200/60">Sat – Sun</dt><dd className="text-white">Closed</dd></div>
              </dl>
              <p className="mt-4 text-sm text-blue-100/70">Cash only. Bring valid photo ID.</p>
              <Link to="/contact" className="mt-5 inline-flex items-center text-sm font-semibold text-blue-300 hover:text-white">Directions and contact <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
            </div>
          </div>
        </aside>
      </div>

      {/* Visit band */}
      <section className="border-t border-white/10 bg-[#002055]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h2 className="text-2xl font-bold text-white">Ready to exchange?</h2>
            <p className="mt-2 text-blue-100/70">Walk in Monday to Friday, 9:30 AM to 6:00 PM. FINTRAC-registered MSB N300001076.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/" className="btn-primary inline-flex items-center">See today's rates <ArrowRight className="w-4 h-4 ml-2" /></Link>
            <Link to="/heroes" className="btn-secondary inline-flex items-center">Frontline Heroes Rate</Link>
          </div>
        </div>
      </section>

      {more.length > 0 && (
        <section className="bg-[#001435]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="flex items-end justify-between mb-8">
              <h2 className="text-2xl font-bold text-white">More guides</h2>
              <Link to="/blog" className="text-sm font-semibold text-blue-300 hover:text-white">View all →</Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {more.map((m) => (
                <Link key={m.id} to={`/blog/${m.slug}`} className="group rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors">
                  {m.image_url && <div className="aspect-[16/9] overflow-hidden"><img src={m.image_url} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>}
                  <div className="p-5">
                    <p className="text-[0.7rem] font-bold uppercase tracking-[0.16em] text-blue-400 mb-2">{category(m)}</p>
                    <h3 className="text-white font-semibold leading-snug group-hover:text-blue-200">{m.title}</h3>
                  </div>
                </Link>))}
            </div>
          </div>
        </section>)}
    </div>
  );
}
